import { z } from 'zod';
import { platformJsonValueSchema } from '@crest/platform-contracts/settings';
import { platformImportResolutionProofSchema } from '@crest/platform-contracts/import-resolution';
export const workforceActivityImportCapability = 'workforce.activity-import.v1';
export const workforceActivityImportMappingVersion = 'workforce-activity-normalized-v1';
export const workforceActivityImportEntityType = 'activity-record';
const timestampSchema = z.string().datetime({ offset: true });
const sourceTimestampSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/, 'Legacy Workforce timestamps must use canonical UTC microsecond precision.');
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const sourceIdSchema = z.string().trim().min(1).max(500);
const tenantKeySchema = z.string().trim().min(1).max(240);
const uuidSchema = z.string().uuid();
export const workforceLegacyActivityCategorySchema = z.enum([
    'late_arrival', 'left_early', 'called_off_sick', 'sick_day', 'personal_day',
    'family_or_emergency', 'no_call_no_show', 'approved_time_off', 'other_attendance',
    'positive_recognition', 'coaching_conversation', 'policy_violation',
    'safety_observation', 'customer_feedback', 'general_note',
]);
export const workforceImportedActivityCategorySchema = z.enum([
    'late-arrival', 'left-early', 'called-off-sick', 'sick-day', 'personal-day',
    'family-or-emergency', 'no-call-no-show', 'approved-time-off', 'other-attendance',
    'positive-recognition', 'coaching-conversation', 'policy-violation',
    'safety-observation', 'customer-feedback', 'general-note',
]);
export const workforceLegacyActivitySourceSchema = z.enum(['manual', 'mr_water']);
export const workforceImportedActivitySourceSchema = z.enum(['manual', 'assistant']);
export const workforceActivityImportSourceSchema = z.strictObject({
    id: sourceIdSchema,
    tenant_id: uuidSchema,
    employee_user_id: uuidSchema,
    section: z.enum(['attendance', 'behavior']),
    category: workforceLegacyActivityCategorySchema,
    occurred_at: sourceTimestampSchema,
    note: z.string().trim().min(1).max(12_000),
    source: workforceLegacyActivitySourceSchema,
    created_by: uuidSchema.nullable(),
    updated_by: uuidSchema.nullable(),
    created_at: sourceTimestampSchema,
    updated_at: sourceTimestampSchema,
});
export const workforceActivityImportRowSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    sourceTable: z.literal('employee_activity_entries'),
    sourceId: sourceIdSchema,
    sourceSha256: sha256Schema,
    sourceUpdatedAt: sourceTimestampSchema,
    payload: workforceActivityImportSourceSchema,
}).superRefine((row, context) => {
    if (row.sourceId !== row.payload.id) {
        context.addIssue({ code: 'custom', path: ['sourceId'], message: 'sourceId must equal payload.id.' });
    }
    if (row.sourceUpdatedAt !== row.payload.updated_at) {
        context.addIssue({ code: 'custom', path: ['sourceUpdatedAt'], message: 'sourceUpdatedAt must equal payload.updated_at.' });
    }
});
export const workforceActivityImportSnapshotSchema = z.strictObject({
    contractVersion: z.literal(workforceActivityImportCapability),
    mappingVersion: z.literal(workforceActivityImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceTenantId: uuidSchema,
    tenantExternalKey: tenantKeySchema,
    targetTenantId: uuidSchema,
    sourceEpoch: z.string().trim().min(1).max(240),
    capturedAt: timestampSchema,
    sourceSha256: sha256Schema,
    membershipSourceMappings: z.array(z.strictObject({
        sourceTenantExternalKey: tenantKeySchema,
        sourceUserId: uuidSchema,
        membershipSourceExternalKey: sourceIdSchema,
    })).max(100_000),
    rows: z.array(z.unknown()).max(100_000),
});
export const workforceActivityMembershipResolutionSchema = z.strictObject({
    sourceUserId: uuidSchema,
    membershipSourceExternalKey: sourceIdSchema,
    sourceTenantExternalKey: tenantKeySchema,
    proof: platformImportResolutionProofSchema,
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.sourceTenantExternalKey !== value.proof.sourceTenantExternalKey) {
        context.addIssue({ code: 'custom', path: ['proof', 'sourceTenantExternalKey'], message: 'Membership proof belongs to another source tenant.' });
    }
    if (value.membershipSourceExternalKey !== value.proof.sourceId) {
        context.addIssue({ code: 'custom', path: ['proof', 'sourceId'], message: 'Membership proof must bind the mapped membership source key.' });
    }
});
export const workforceActivityImportTargetPayloadSchema = z.strictObject({
    membershipId: uuidSchema,
    section: z.enum(['attendance', 'behavior']),
    category: workforceImportedActivityCategorySchema,
    occurredAt: timestampSchema,
    note: z.string().trim().min(1).max(12_000),
    source: workforceImportedActivitySourceSchema,
    revision: z.literal(1),
    createdByMembershipId: uuidSchema,
    updatedByMembershipId: uuidSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const workforceActivityImportRejectReasonSchema = z.enum([
    'schema-invalid',
    'source-tenant-mismatch',
    'duplicate-source-identity',
    'source-after-capture',
    'missing-historical-actor',
    'unresolved-membership',
    'cross-tenant-membership',
    'invalid-section-category',
]);
export const workforceActivityImportRejectSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(workforceActivityImportEntityType),
    sourceId: sourceIdSchema,
    sourceSha256: sha256Schema,
    reasonCode: workforceActivityImportRejectReasonSchema,
    fieldPaths: z.array(z.string().trim().min(1).max(240)).min(1).max(20),
});
export const workforceActivityImportIdentityStrategySchema = z.enum(['preserved-uuid', 'deterministic-v5']);
export const workforceActivityImportPlanActionSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(workforceActivityImportEntityType),
    sourceId: sourceIdSchema,
    sourceSha256: sha256Schema,
    sourceUpdatedAt: sourceTimestampSchema,
    sourceTenantExternalKey: tenantKeySchema,
    targetTenantId: uuidSchema,
    targetId: uuidSchema,
    versionId: uuidSchema,
    identityStrategy: workforceActivityImportIdentityStrategySchema,
    payload: workforceActivityImportTargetPayloadSchema,
    sourceEvidence: workforceActivityImportSourceSchema,
    employeeResolution: workforceActivityMembershipResolutionSchema,
    creatorResolution: workforceActivityMembershipResolutionSchema,
    updaterResolution: workforceActivityMembershipResolutionSchema,
});
export const workforceActivityImportPlanSchema = z.strictObject({
    contractVersion: z.literal(workforceActivityImportCapability),
    mappingVersion: z.literal(workforceActivityImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceTenantId: uuidSchema,
    tenantExternalKey: tenantKeySchema,
    targetTenantId: uuidSchema,
    sourceEpoch: z.string().trim().min(1).max(240),
    capturedAt: timestampSchema,
    sourceSha256: sha256Schema,
    planSha256: sha256Schema,
    actions: z.array(workforceActivityImportPlanActionSchema),
    rejects: z.array(workforceActivityImportRejectSchema),
    counts: z.strictObject({
        sourceRows: z.number().int().nonnegative(),
        plannedRows: z.number().int().nonnegative(),
        rejectedRows: z.number().int().nonnegative(),
    }),
}).superRefine((value, context) => {
    if (value.counts.sourceRows !== value.actions.length + value.rejects.length) {
        context.addIssue({ code: 'custom', path: ['counts'], message: 'Every source row must produce one action or reject.' });
    }
});
export const workforceActivityImportExecutionReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(workforceActivityImportEntityType),
    sourceId: sourceIdSchema,
    sourceSha256: sha256Schema,
    sourceUpdatedAt: sourceTimestampSchema,
    targetId: uuidSchema,
    identityStrategy: workforceActivityImportIdentityStrategySchema,
    outcome: z.enum(['applied', 'skipped']),
    previousTargetSha256: sha256Schema.nullable(),
    targetSha256: sha256Schema,
    targetCanonical: workforceActivityImportTargetPayloadSchema,
    rollbackToken: platformJsonValueSchema,
});
export const workforceActivityImportRollbackReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    outcome: z.enum(['applied', 'already-rolled-back', 'rejected-conflict', 'rejected-state']),
    detailCode: z.string().trim().min(3).max(120),
    targetSha256: sha256Schema.nullable(),
});
//# sourceMappingURL=workforce-import.contract.js.map